/**
 * Shared type definitions used across the application
 * This module contains common types to avoid circular dependencies
 */

export const propertyTypes = [
  "studio",
  "apartment",
  "warehouse",
  "house",
  "medical_office",
  "building",
  "farm",
  "room",
  "hotel",
  "commercial_space",
  "lot",
  "office",
  "parking",
] as const;

export type PropertyType = (typeof propertyTypes)[number];

export const listingTypes = ["rent", "sale", "rent_and_sale"] as const;

export const listingPurposes = ["living", "business"] as const;

export type ListingPurpose = (typeof listingPurposes)[number];

export type ListingType = (typeof listingTypes)[number];

export type ListingTypeEnglish = "sale" | "rent";
export type PropertyTypeEnglish = PropertyType;

export const crmTypes = [
  "simi",
  "mobilia",
  "domus",
  "softim",
  "paco",
  "googlesheets",
  "bienraiz",
  "smarthome",
  "reda",
  "wasi",
] as const;

export type CrmType = (typeof crmTypes)[number];

// Type assertion helper for Zod enum
// Spread into a new array to convert readonly array to mutable tuple for Zod
export const crmTypesTuple = [...crmTypes] as [string, ...string[]];

export const erpTypes = ["nuwwe", "simi"] as const;

export type ErpType = (typeof erpTypes)[number];

// Type assertion helper for Zod enum
// Spread into a new array to convert readonly array to mutable tuple for Zod
export const erpTypesTuple = [...erpTypes] as [string, ...string[]];

export const ticketSystemTypes = ["palomma", "gestium", "nuwwe"] as const;

export type TicketSystemType = (typeof ticketSystemTypes)[number];

export const ticketSystemTypesTuple = [...ticketSystemTypes] as [string, ...string[]];

export const leadEventTypes = [
  "lead.created",
  "lead.first-message-sent",
  "lead.first-message-delivered",
  "lead.first-message-read",
  "lead.first-message-answered",
  "lead.redirected-to-human",
  "lead.appointment-intention",
  "lead.appointment-created",
  "lead.inactivated",
  "lead.discarded",
  "lead.call-ended",
  "lead.property-info-collected",
  "lead.maintenance-requested",
] as const;

// Type assertion helper for Zod enum
// Spread into a new array to convert readonly array to mutable tuple for Zod
export const leadEventTypesTuple = [...leadEventTypes] as [string, ...string[]];

export type LeadEventType = (typeof leadEventTypes)[number];

export const pipedriveConditionTypes = [
  "noInteraction",
  "interaction",
  "appointmentIntention",
] as const;

export type PipedriveConditionType = (typeof pipedriveConditionTypes)[number];

export const crmSalesProviders = [
  "zoho",
  "pipedrive",
  "bitrix",
  "custom",
  "smarthome-rentals",
  "softinm",
  "domus",
  "salesforce",
  "simi",
  "hubspot",
  "internal", // Palomma-authored bespoke integrations (registry-dispatched)
] as const;

export type CrmSalesProvider = (typeof crmSalesProviders)[number];

// Type assertion helper for Zod enum
// Spread into a new array to convert readonly array to mutable tuple for Zod
export const crmSalesProvidersTuple = [...crmSalesProviders] as [string, ...string[]];

// Alias for backward compatibility and clarity in self-config context
export type CrmSelfConfigProvider = CrmSalesProvider;

export const AGENT_TOPIC_SOURCEPROPERTIES = "rentals-sourceproperties";
export const AGENT_TOPIC_SALES = "rentals-sales";
export const AGENT_TOPIC_MAINTENANCE = "rentals-maintenance";
export const AGENT_TOPIC_INQUIRIES = "rentals-inquiries";
export const AGENT_TOPIC_CUSTOM = "rentals-custom";
export const AGENT_TOPIC_COLLECTIONS = "rentals-collections";

export const agentTopics = [
  AGENT_TOPIC_SOURCEPROPERTIES,
  AGENT_TOPIC_SALES,
  AGENT_TOPIC_MAINTENANCE,
  AGENT_TOPIC_INQUIRIES,
  AGENT_TOPIC_CUSTOM,
  AGENT_TOPIC_COLLECTIONS,
] as const;

export type AgentTopic = (typeof agentTopics)[number];

// Type assertion helper for Zod enum
// Spread into a new array to convert readonly array to mutable tuple for Zod
export const agentTopicsTuple = [...agentTopics] as [string, ...string[]];

// Standard pipeline ids (match core/pipeline; used by frontend for display/matching)
export const PIPELINE_ID_DEFAULT = "default";
export const PIPELINE_ID_SOURCEPROPERTIES = "sourceproperties";
export const PIPELINE_ID_MAINTENANCE = "maintenance";
export const PIPELINE_ID_GENERAL = "general";
export const PIPELINE_ID_COLLECTIONS = "collections";

export const STANDARD_PIPELINE_IDS = [
  PIPELINE_ID_DEFAULT,
  PIPELINE_ID_SOURCEPROPERTIES,
  PIPELINE_ID_MAINTENANCE,
  PIPELINE_ID_GENERAL,
  PIPELINE_ID_COLLECTIONS,
] as const;
